import { Component, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { EnergyInterfaceResponse } from '../../../../core/clients/omni-core/models';
import { EnergyInterface, energyInterfaceToModel } from './definitions';
import {
  EnergyInterfacesStore,
  toEnergyInterfacePayload,
} from '../../stores/energy-interfaces.store';
import {
  NotificationService,
  Button,
  InputSimple,
  Select,
  HelpButton,
  Badge,
  SimpleDialog,
  SimpleDialogLayout,
} from 'excellence-ui';
import { FormField, form, required, validate } from '@angular/forms/signals';
import { NgIcon } from '@ng-icons/core';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { EnergyInterfaceDetailsEditSuccessNotification } from '../../notifications/energy-interface-details-edit-success-notification/energy-interface-details-edit-success-notification';
import { validateMalo } from '../../validators/malo.validator';
import { LocationsStore } from '../../../locations/store/locations.store';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../../../environments/environment';
import { LocationNamePipe } from '../../../locations/pipes/location-name-pipe';

@Component({
  selector: 'app-energy-interface-details-edit-form',
  templateUrl: './energy-interface-details-edit-form.html',
  imports: [
    Button,
    NgIcon,
    InputSimple,
    FieldErrors,
    FormField,
    Select,
    HelpButton,
    TranslocoPipe,
    LocationNamePipe,
    Badge,
    SimpleDialog,
    SimpleDialogLayout,
  ],
})
export class EnergyInterfaceDetailsEditForm {
  private store = inject(EnergyInterfacesStore);
  private notificationService = inject(NotificationService);
  protected locationsStore = inject(LocationsStore);
  private translocoService = inject(TranslocoService);

  protected maloMeloMeterArticleUrl = environment.articles.meloMaloMeter;

  energyInterface = input.required<EnergyInterfaceResponse>();
  private energyInterfaceData = computed(() => energyInterfaceToModel(this.energyInterface()));
  organizationId = input.required<string>();
  energyInterfaceId = input.required<string>();

  private state = computed(() =>
    this.store.energyInterfaces()?.find((ei) => ei.id === this.energyInterfaceId()),
  );

  protected editButtonLoading = signal(false);
  protected enableEdit = signal(false);
  protected isUpdateLoading = computed(() => this.state()?.isEditLoading ?? false);

  protected model = linkedSignal<EnergyInterface>(() =>
    energyInterfaceToModel(this.energyInterface()),
  );
  protected energyInterfaceStatus = computed(() => this.state()?.data?.status ?? 'UNKNOWN');
  protected selectedLocation = computed(() => {
    const locationId = this.model().locationID;
    if (!locationId) {
      return undefined;
    }
    return this.locationsStore.filteredLocations().find((loc) => loc.id === locationId);
  });

  protected form = form(this.model, (schemaPath) => {
    required(schemaPath.label);
    required(schemaPath.utilityType);
    required(schemaPath.measurementType);
    required(schemaPath.locationID);
    // malo
    validate(schemaPath.maloID, ({ value }) => {
      const maloID = value();
      if (maloID === '') {
        return null; // Malo ID is optional
      }
      try {
        validateMalo(maloID);
        return null;
      } catch {
        return {
          kind: 'malo',
          message: this.translocoService.translate(
            'cockpit.energyInterfaces.details.general.maloIDInvalid',
          ),
        };
      }
    });
  });

  protected onClickCancel() {
    this.enableEdit.set(false);
    this.model.set(this.energyInterfaceData());
  }

  protected async onClickEnableEdit() {
    this.editButtonLoading.set(true);
    await new Promise((resolve) => setTimeout(resolve, environment.delays.editEnergyInterfaceMs));
    this.enableEdit.set(true);
    this.editButtonLoading.set(false);
  }

  protected async onSubmit() {
    if (this.form().invalid()) {
      return;
    }
    try {
      await this.store.update(
        this.energyInterfaceId(),
        toEnergyInterfacePayload(this.model()),
        environment.delays.updateEnergyInterfaceMs,
      );
      this.enableEdit.set(false);
      this.notificationService.show({
        component: EnergyInterfaceDetailsEditSuccessNotification,
        duration: 5000,
        componentProps: {
          clickUndoCallback: () => this.onClickUndo(),
        },
      });
    } catch (e) {
      console.error('Failed to update energy interface', e);
      this.notificationService.showError({
        title: this.translocoService.translate(
          'cockpit.energyInterfaces.details.general.updateErrorTitle',
        ),
      });
    }
  }

  private onClickUndo() {
    return this.store.undoUpdate(
      this.energyInterfaceId(),
      environment.delays.updateEnergyInterfaceMs,
    );
  }
}
